import { Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { Utilities } from '../utilities/utilities';

@Injectable({ providedIn: 'root' })
export class BeaconKeyboardLifterService {
  private renderer: Renderer2;
  private beaconEl: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  private vvResizeHandler: any;
  private vvScrollHandler: any;
  private rafId: number | null = null;
  private didShowHandle: any;
  private didHideHandle: any;
  private lastKeyboardHeight = 0;
  private isActive = false;
  private isChatActive = false;
  private readonly gap = 4;
  private readonly minTopGap = 8;
  private readonly offscreenTopAllowance = 120;
  private readonly debug = true;
  private beaconReadyIntervalId: number | null = null;
  private beaconHandlers: {
    open?: () => void;
    close?: () => void;
    ready?: () => void;
    articleViewed?: (data?: any) => void;
    chatStarted?: (data?: any) => void;
    emailSent?: (data?: any) => void;
    messageClicked?: (data?: any) => void;
    messageClosed?: (data?: any) => void;
    messageTriggered?: (data?: any) => void;
    search?: (data?: any) => void;
  } = {};

  constructor(private zone: NgZone, rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public init() {
    if (this.isActive) {
      return;
    }
    if (!Utilities.isCapacitorNative()) {
      return;
    }
    const platform = this.getPlatform();
    // Disable all keyboard lifting on iOS
    if (platform === 'ios') {
      return;
    }
    this.isActive = true;
    this.attachObserver();
    this.attachVisualViewportListeners();
    this.attachKeyboardListeners();
    this.attachBeaconListeners();
  }

  public destroy() {
    this.isActive = false;
    this.isChatActive = false;
    this.lastKeyboardHeight = 0;
    this.detachVisualViewportListeners();
    this.detachObserver();
    this.removeKeyboardListeners();
    this.detachBeaconListeners();
    this.clearTransform();
  }

  private getPlatform(): 'ios' | 'android' | 'web' {
    try {
      return Capacitor.getPlatform() as any;
    } catch (_) {
      return 'web';
    }
  }

  private attachObserver() {
    if (this.observer) {
      return;
    }
    this.observer = new MutationObserver(() => {
      this.findBeacon();
      this.scheduleUpdate();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.findBeacon();
  }

  private detachObserver() {
    if (!this.observer) {
      return;
    }
    this.observer.disconnect();
    this.observer = null;
  }

  private findBeacon() {
    const el =
      (document.querySelector('#beacon-container .BeaconContainer') as HTMLElement | null) ||
      (document.querySelector('#beacon-container iframe') as HTMLElement | null) ||
      (document.getElementById('beacon-container') as HTMLElement | null);
    if (!el || el === this.beaconEl) {
      return;
    }
    this.beaconEl = el;
    this.applyStyleHints();
  }

  private applyStyleHints() {
    if (!this.beaconEl) {
      return;
    }
    this.renderer.setStyle(this.beaconEl, 'will-change', 'transform');
    this.renderer.setStyle(this.beaconEl, 'transition', 'transform 60ms linear');
  }

  private attachKeyboardListeners() {
    if (!Capacitor.isPluginAvailable('Keyboard')) {
      return;
    }
    try {
      this.didShowHandle = Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
        this.zone.runOutsideAngular(() => this.onShow(info));
      });
      this.didHideHandle = Keyboard.addListener('keyboardDidHide', () => {
        this.zone.runOutsideAngular(() => this.onHide());
      });
    } catch (_) {
      // noop
    }
  }

  private removeKeyboardListeners() {
    try {
      if (this.didShowHandle && this.didShowHandle.remove) {
        this.didShowHandle.remove();
        this.didShowHandle = null;
      }
      if (this.didHideHandle && this.didHideHandle.remove) {
        this.didHideHandle.remove();
        this.didHideHandle = null;
      }
    } catch (_) {
      // noop
    }
  }

  private attachVisualViewportListeners() {
    try {
      const vv: any = (window as any).visualViewport;
      if (!vv) {
        return;
      }
      if (!this.vvResizeHandler) {
        this.vvResizeHandler = () => this.scheduleUpdate();
        vv.addEventListener('resize', this.vvResizeHandler);
      }
      if (!this.vvScrollHandler) {
        this.vvScrollHandler = () => this.scheduleUpdate();
        vv.addEventListener('scroll', this.vvScrollHandler);
      }
    } catch (_) {
      // noop
    }
  }

  private detachVisualViewportListeners() {
    try {
      const vv: any = (window as any).visualViewport;
      if (!vv) {
        return;
      }
      if (this.vvResizeHandler) {
        vv.removeEventListener('resize', this.vvResizeHandler);
        this.vvResizeHandler = null;
      }
      if (this.vvScrollHandler) {
        vv.removeEventListener('scroll', this.vvScrollHandler);
        this.vvScrollHandler = null;
      }
    } catch (_) {
      // noop
    }
  }

  private scheduleUpdate() {
    if (!this.isActive || !this.isChatActive) {
      return;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => this.updateLift());
  }

  private onShow(info: KeyboardInfo) {
    this.lastKeyboardHeight = info?.keyboardHeight || 0;
    this.findBeacon();
    if (this.beaconEl) {
      this.isChatActive = true;
    }
    if (this.debug) {
      console.info('[BeaconLifter] keyboardDidShow', { keyboardHeight: this.lastKeyboardHeight });
    }
    this.scheduleUpdate();
  }

  private onHide() {
    this.lastKeyboardHeight = 0;
    this.isChatActive = false;
    if (this.debug) {
      console.info('[BeaconLifter] keyboardDidHide');
    }
    this.clearTransform();
  }

  private clearTransform() {
    if (!this.beaconEl) {
      return;
    }
    this.renderer.setStyle(this.beaconEl, 'transform', '');
  }

  private attachBeaconListeners() {
    const w: any = window as any;
    const attach = () => {
      if (!w || typeof w.Beacon !== 'function') {
        return false;
      }
      if (this.beaconHandlers.open || this.beaconHandlers.close) {
        return true;
      }
      this.beaconHandlers.open = () => {
        console.info('[BeaconLifter] open');
        this.findBeacon();
        this.scheduleUpdate();
      };
      this.beaconHandlers.close = () => {
        console.info('[BeaconLifter] close');
        this.isChatActive = false;
        this.clearTransform();
      };
      this.beaconHandlers.ready = () => {
        console.info('[BeaconLifter] ready');
      };
      this.beaconHandlers.articleViewed = (data?: any) => {
        console.info('[BeaconLifter] article-viewed', data);
      };
      this.beaconHandlers.chatStarted = (data?: any) => {
        console.info('[BeaconLifter] chat-started', data);
        this.isChatActive = true;
        this.findBeacon();
        this.scheduleUpdate();
      };
      this.beaconHandlers.emailSent = (data?: any) => {
        console.info('[BeaconLifter] email-sent', data);
      };
      this.beaconHandlers.messageClicked = (data?: any) => {
        console.info('[BeaconLifter] message-clicked', data);
      };
      this.beaconHandlers.messageClosed = (data?: any) => {
        console.info('[BeaconLifter] message-closed', data);
      };
      this.beaconHandlers.messageTriggered = (data?: any) => {
        console.info('[BeaconLifter] message-triggered', data);
      };
      this.beaconHandlers.search = (data?: any) => {
        console.info('[BeaconLifter] search', data);
      };
      try {
        w.Beacon('on', 'open', this.beaconHandlers.open);
        w.Beacon('on', 'close', this.beaconHandlers.close);
        w.Beacon('on', 'ready', this.beaconHandlers.ready);
        w.Beacon('on', 'article-viewed', this.beaconHandlers.articleViewed);
        w.Beacon('on', 'chat-started', this.beaconHandlers.chatStarted);
        w.Beacon('on', 'email-sent', this.beaconHandlers.emailSent);
        w.Beacon('on', 'message-clicked', this.beaconHandlers.messageClicked);
        w.Beacon('on', 'message-closed', this.beaconHandlers.messageClosed);
        w.Beacon('on', 'message-triggered', this.beaconHandlers.messageTriggered);
        w.Beacon('on', 'search', this.beaconHandlers.search);
      } catch (_) {
        // noop
      }
      return true;
    };

    if (attach()) {
      return;
    }

    // Beacon loads asynchronously; poll briefly until it's ready.
    if (this.beaconReadyIntervalId) {
      return;
    }
    this.beaconReadyIntervalId = window.setInterval(() => {
      if (attach()) {
        if (this.beaconReadyIntervalId) {
          clearInterval(this.beaconReadyIntervalId);
          this.beaconReadyIntervalId = null;
        }
      }
    }, 500);
  }

  private detachBeaconListeners() {
    const w: any = window as any;
    if (this.beaconReadyIntervalId) {
      clearInterval(this.beaconReadyIntervalId);
      this.beaconReadyIntervalId = null;
    }
    if (!w || typeof w.Beacon !== 'function') {
      this.beaconHandlers = {};
      return;
    }
    try {
      if (this.beaconHandlers.open) {
        w.Beacon('off', 'open', this.beaconHandlers.open);
      }
      if (this.beaconHandlers.chatStarted) {
        w.Beacon('off', 'chat-started', this.beaconHandlers.chatStarted);
      }
      if (this.beaconHandlers.close) {
        w.Beacon('off', 'close', this.beaconHandlers.close);
      }
      if (this.beaconHandlers.ready) {
        w.Beacon('off', 'ready', this.beaconHandlers.ready);
      }
      if (this.beaconHandlers.articleViewed) {
        w.Beacon('off', 'article-viewed', this.beaconHandlers.articleViewed);
      }
      if (this.beaconHandlers.emailSent) {
        w.Beacon('off', 'email-sent', this.beaconHandlers.emailSent);
      }
      if (this.beaconHandlers.messageClicked) {
        w.Beacon('off', 'message-clicked', this.beaconHandlers.messageClicked);
      }
      if (this.beaconHandlers.messageClosed) {
        w.Beacon('off', 'message-closed', this.beaconHandlers.messageClosed);
      }
      if (this.beaconHandlers.messageTriggered) {
        w.Beacon('off', 'message-triggered', this.beaconHandlers.messageTriggered);
      }
      if (this.beaconHandlers.search) {
        w.Beacon('off', 'search', this.beaconHandlers.search);
      }
    } catch (_) {
      // noop
    }
    this.beaconHandlers = {};
  }

  private updateLift() {
    // Temporarily disabled for testing.
    return;
    if (!this.isActive || !this.isChatActive || !this.beaconEl) {
      return;
    }
    const rect = this.beaconEl.getBoundingClientRect();
    if (!isFinite(rect.bottom)) {
      this.clearTransform();
      return;
    }
    const vv: any = (window as any).visualViewport;
    const vvBottom = (vv && typeof vv.height === 'number')
      ? (vv.height + (vv.offsetTop || 0))
      : NaN;
    const bodyPad = parseFloat((window.getComputedStyle(document.body).paddingBottom || '0').replace('px', '')) || 0;
    const vvShorter = (vv && typeof vv.height === 'number')
      ? (vvBottom < (window.innerHeight - 1))
      : false;

    const signals: number[] = [];
    if (!isNaN(vvBottom)) {
      signals.push(vvBottom);
    }
    if (bodyPad > 0) {
      signals.push(window.innerHeight - bodyPad);
    }
    if (this.lastKeyboardHeight > 0) {
      signals.push(window.innerHeight - this.lastKeyboardHeight);
    }
    // Use the minimum signal to keep the beacon above the keyboard.
    // Fall back to full height if no keyboard-related signals exist.
    const visibleBottom =
      signals.length > 0 ? Math.min.apply(Math, signals) : window.innerHeight;
    if (!isFinite(visibleBottom)) {
      this.clearTransform();
      return;
    }

    const isKeyboardOpen = this.isKeyboardOpen();
    if (!isKeyboardOpen) {
      this.clearTransform();
      return;
    }

    const overlap = (rect.bottom + this.gap) - visibleBottom;
    if (!isFinite(overlap)) {
      this.clearTransform();
      return;
    }
    let lift = Math.max(0, overlap);
    if (this.lastKeyboardHeight > 0) {
      const maxLift = this.lastKeyboardHeight + this.gap;
      lift = Math.min(lift, maxLift);
    }
    // Clamp so the top doesn't go too far off-screen.
    const maxLiftForTop = Math.max(
      0,
      rect.top + this.offscreenTopAllowance - this.minTopGap
    );
    lift = Math.min(lift, maxLiftForTop);
    if (this.debug) {
      console.info('[BeaconLifter] update', {
        tag: this.beaconEl.tagName,
        rectBottom: rect.bottom,
        rectTop: rect.top,
        vvBottom,
        bodyPad,
        vvShorter,
        keyboardHeight: this.lastKeyboardHeight,
        visibleBottom,
        overlap,
        lift,
        maxLiftForTop
      });
    }
    this.renderer.setStyle(
      this.beaconEl,
      'transform',
      lift ? `translate3d(0, -${lift}px, 0)` : ''
    );
  }

  private isKeyboardOpen(): boolean {
    // Only lift after a confirmed keyboardDidShow event.
    return this.lastKeyboardHeight > 0;
  }
}
